import {
    CreateForecastDTO,
    CustomerMetricsDeleteResponse,
    CustomerMetricsDTO,
    CustomerMetricsType,
    CustomerSegmentWithRelations,
    DayTimeSlot,
    ExportCustomerMetricsResponse,
    ForecastDataDTO,
    ForecastDataGroupedByType,
    ForecastRunWithForecast,
    ForecastWithRelations,
    ForecastWithRequiredRelations,
    HistoricVolumeByWorkingUnitUploadResult,
    HistoricVolumeBatchUploadResult,
    UploadCustomerMetricsResponse,
    ForecastsCopyToOfferedParamsDTO,
    ForecastsCopyToOfferedResultDTO,
} from '@callmart/types';
import { extractContentType, extractFileName, XLSX_CONTENT_TYPE } from '../utils/fetchUtils';
import api from './index';

const BASE = '/forecasts';

const uploadCustomerMetricsFile = async (file: File, type?: CustomerMetricsType) => {
    const formData = new FormData();
    formData.append('file', file);
    if (type) formData.append('type', type);

    const response = await api.post<UploadCustomerMetricsResponse[]>(`${BASE}/customer-metrics/upload`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return response.data;
};

export const uploadCustomerMetrics = async (file: File) => {
    return uploadCustomerMetricsFile(file);
};

export const exportCustomerMetrics = async (type?: CustomerMetricsType): Promise<ExportCustomerMetricsResponse> => {
    const response = await api.get<Blob>(`${BASE}/customer-metrics/export`, {
        params: { type },
        responseType: 'blob',
    });

    return {
        data: response.data,
        contentType: extractContentType(response.headers) || XLSX_CONTENT_TYPE,
        fileName: extractFileName(response.headers) || `export.xlsx`,
    };
};

export const getCustomerMetrics = async ({ type }: { type: CustomerMetricsType }) => {
    const { data } = await api.get<CustomerMetricsDTO[]>(`${BASE}/customer-metrics`, {
        params: { type },
    });
    return data;
};

export const deleteCustomerMetrics = async ({ ids, type }: { ids: number[]; type?: CustomerMetricsType }) => {
    const { data } = await api.delete<CustomerMetricsDeleteResponse>(`${BASE}/customer-metrics`, {
        data: { ids, type },
    });

    return data;
};

export const deleteHistoricVolume = async (id: number) => {
    const { data } = await api.delete<{ success: boolean; message: string }>(`${BASE}/historic-volumes/${id}`);
    return data;
};

export const deleteHistoricVolumesByWorkingUnit = async (workingUnitId: number) => {
    const { data } = await api.delete<{ success: boolean; message: string }>(
        `${BASE}/historic-volumes/by-working-unit/${workingUnitId}`
    );
    return data;
};

export const getCustomerSegments = async () => {
    const { data } = await api.get<CustomerSegmentWithRelations[]>(`${BASE}/customer-segments`);
    return data;
};

export const getDayTimeSlots = async () => {
    const { data } = await api.get<DayTimeSlot[]>(`${BASE}/time-slots`);
    return data;
};

export const getForecast = async (id: number, runOffset?: number | string | null) => {
    // Only include run param for non-zero offsets (0 means latest, which is the default)
    const shouldIncludeRunParam = runOffset !== null && runOffset !== undefined && runOffset !== 0;
    const { data } = await api.get<ForecastWithRelations>(`${BASE}/${id}`, {
        params: shouldIncludeRunParam ? { run: runOffset } : undefined,
    });
    return data;
};

export const getForecasts = async () => {
    const { data } = await api.get<ForecastWithRequiredRelations[]>(`${BASE}`);
    return data;
};

export const getForecastPredictions = async (runId: number, params: ForecastDataDTO) => {
    const { data } = await api.get<ForecastDataGroupedByType[]>(`${BASE}/${runId}/predictions`, {
        params,
    });
    return data;
};

export interface ForecastResultForApiRow {
    dateTime: string;
    date: string;
    time: string;
    duration: number;
    value: number;
}

export interface ForecastResultsForApiResponse {
    forecast: { id: number; latestRunId: number | null } | null;
    data: ForecastResultForApiRow[];
}

export const getForecastResultsForApi = async (params: {
    workingUnitId: number;
    dateFrom: string;
    dateTo: string;
}): Promise<ForecastResultsForApiResponse | null> => {
    try {
        const { data } = await api.get<ForecastResultsForApiResponse>(`${BASE}/results`, { params });
        return data;
    } catch (error: any) {
        // 404 = no forecast for this working unit; treat as empty rather than throwing
        if (error?.response?.status === 404) return null;
        throw error;
    }
};

export const getForecastHistoricData = async (forecastId: number, params: ForecastDataDTO) => {
    const { data } = await api.get<ForecastDataGroupedByType[]>(`${BASE}/${forecastId}/historic-data`, {
        params,
    });
    return data;
};

export const createForecast = async (form: CreateForecastDTO) => {
    const { data } = await api.post<ForecastWithRequiredRelations>(`${BASE}`, form);
    return data;
};

export const updateForecast = async (id: number, form: CreateForecastDTO) => {
    const { data } = await api.put<ForecastWithRequiredRelations>(`${BASE}/${id}`, form);
    return data;
};

export const startForecast = async (id: number) => {
    const { data } = await api.post<ForecastRunWithForecast>(`${BASE}/${id}/start`);
    return data;
};

export const deleteForecast = async (id: number) => {
    const { data } = await api.delete<{ success: boolean }>(`${BASE}/${id}`);
    return data;
};

export const exportForecastResults = async (runId: number) => {
    const response = await api.get<Blob>(`${BASE}/runs/${runId}/export-excel`, {
        responseType: 'blob',
    });

    return {
        data: response.data,
        contentType: extractContentType(response.headers) || XLSX_CONTENT_TYPE,
        fileName: extractFileName(response.headers) || `forecast-export.xlsx`,
    };
};

export const copyForecastsToOffered = async (params: ForecastsCopyToOfferedParamsDTO) => {
    const { data } = await api.post<ForecastsCopyToOfferedResultDTO>(`${BASE}/copy-to-offered`, params);
    return data;
};

export const uploadHistoricVolumeBatch = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<HistoricVolumeBatchUploadResult>(
        `${BASE}/historic-volumes/upload-batch`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    return response.data;
};

export const uploadHistoricVolumeByWorkingUnit = async (file: File, workingUnitId: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workingUnitId', workingUnitId.toString());

    const response = await api.post<HistoricVolumeByWorkingUnitUploadResult>(
        `${BASE}/historic-volumes/upload-by-working-unit`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    return response.data;
};
